import { useMutation, useQuery } from '@tanstack/react-query';
import { Alert, App, Button, Drawer, Form, Input, Select, Space } from 'antd';
import {
  ACCESS_ROLES,
  ACCESS_ROLE_RANK,
  actingOrgUnitIdOf,
  createOfficerSchema,
  updateOfficerSchema,
  type AccessRole,
  type OfficerDto,
} from '@pams/shared';
import { UnitElevationReadout } from '@/components/elevation/UnitElevation';
import { OrgUnitPicker } from '@/components/OrgUnitPicker';
import { PhoneInput } from '@/components/PhoneInput';
import { RankPicker } from '@/components/RankPicker';
import { useAuth } from '@/hooks/useAuth';
import type { ApiError } from '@/lib/api';
import { api } from '@/lib/api';
import { accessRoleLabel } from '@/lib/labels';
import { fetchScopedOrgTree } from '@/lib/orgTree';
import { queryKeys } from '@/lib/queryKeys';
import { withChosenOption } from '@/lib/selectOptions';
import { applyServerIssues, useZodForm } from '@/lib/zodForm';
import { actingOrgUnitTypeOf } from '@/lib/accessRole';

/**
 * Create/edit an officer.
 *
 * ⚠️ `orgUnitId` and `rankId` appear on CREATE only. Changing either afterwards goes
 * through Transfer, because FR-3.1 requires it to write a posting-history row —
 * `updateOfficerSchema` does not carry them, so zod would strip them anyway.
 */
export const OfficerDrawer = ({
  open,
  mode,
  officer,
  readOnly,
  onClose,
  onSuccess,
}: {
  open: boolean;
  mode: 'create' | 'edit';
  officer?: OfficerDto;
  readOnly: boolean;
  onClose: () => void;
  onSuccess: () => void;
}) => {
  const { message } = App.useApp();
  const { officer: actor } = useAuth();
  const schema = mode === 'create' ? createOfficerSchema : updateOfficerSchema;
  /*
   * [P4-UX] Labels for the summary sentence. Only the fields whose humanised path reads
   * badly are named; `name`, `email` and `phone` need nothing.
   */
  const { form, field, submit, formErrors, setFormErrors } = useZodForm(schema, {
    labels: { rankId: 'Rank', orgUnitId: 'Posted to', serviceNumber: 'Service / collar number' },
  });

  /**
   * A caller may not grant a role at or above their own — the same rule the API applies
   * with the same table. Filtering the options here means the UI does not offer a choice
   * that would come back a 403.
   */
  /*
   * ⚠️ [amended C4a §8] THE THIRD VOCABULARY IS GONE. This drawer lower-cased the enum
   * (`nodal admin`, `None (mobile only)`) while the list and the avatar menu used `labels.ts`.
   * The options are now named for the officer's POSTING — `officer.orgUnit` when editing (the
   * posting changes only through Transfer), the unit chosen in "Posted to" when creating, read
   * from the org tree `OrgUnitPicker` has already fetched. Until a posting is chosen, the words
   * name no node.
   * ⚠️ [amended C5c §11g] …AND FOR THE LEVEL THAT POSTING ACTS AT, IN BOTH MODES. Editing reads the
   * row's `actingOrgUnit`; creating resolves the chosen unit's acting unit from the same tree with
   * `actingOrgUnitIdOf` — the one derivation — so a nodal admin created at a division set one level
   * up is granted "HQ Admin", the word the list names them by the moment they exist. An acting unit
   * outside the caller's tree falls back to the posting; the server refuses that create anyway.
   */
  const chosenOrgUnitId = Form.useWatch('orgUnitId', form) as string | undefined;
  /* ⚠️ [amended OTHERS-EXPAND] The SAME fetcher as `OrgUnitPicker`, whose entry this shares: a
     single-page `queryFn` here would truncate the picker's tree whenever this ran first. */
  const orgTree = useQuery({
    queryKey: queryKeys.orgUnits.tree(),
    queryFn: ({ signal }) => fetchScopedOrgTree(signal),
    enabled: open && mode === 'create',
  });
  const chosenUnit = orgTree.data?.items.find((unit) => unit.id === chosenOrgUnitId);
  const actingUnitId = chosenUnit
    ? actingOrgUnitIdOf({ _id: chosenUnit.id, ancestors: chosenUnit.ancestors, elevation: chosenUnit.elevation })
    : undefined;
  const postingType =
    mode === 'edit'
      ? actingOrgUnitTypeOf(officer)
      : (orgTree.data?.items.find((unit) => unit.id === actingUnitId) ?? chosenUnit)?.type;
  const roleLabel = (role: AccessRole): string => accessRoleLabel(role, postingType);

  const grantableRoles = ACCESS_ROLES.filter(
    (role) => actor && ACCESS_ROLE_RANK[actor.accessRole] > ACCESS_ROLE_RANK[role],
  );

  const save = useMutation({
    mutationFn: (values: Record<string, unknown>) =>
      mode === 'create'
        ? api.post<OfficerDto>('/officers', values)
        : api.patch<OfficerDto>(`/officers/${officer?.id ?? ''}`, values),
    onSuccess: () => {
      void message.success(mode === 'create' ? 'Officer created' : 'Officer updated');
      onSuccess();
      onClose();
    },
    onError: (error: ApiError) => {
      const remaining = applyServerIssues(form, error.details);
      setFormErrors(remaining.length > 0 ? remaining : [error.message]);
    },
  });

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={520}
      /* [P2] Opening a row without the write capability is VIEWING, and the title says
         so rather than promising an edit every control refuses. */
      title={mode === 'create' ? 'New officer' : `${readOnly ? 'View' : 'Edit'} ${officer?.name ?? ''}`}
      extra={
        <Space>
          {/* ⚠️ "Done", not "Close": the Drawer already ships a × whose accessible name IS
              "Close", and two controls with one name in a dialog is the same defect the
              row-action doctrine warns about. */}
          <Button onClick={onClose}>{readOnly ? 'Done' : 'Cancel'}</Button>
          {/* Absent rather than disabled: there is nothing here to save, and a greyed
              Save under a title that says View is telling the reader twice. */}
          {!readOnly && (
            <Button type="primary" loading={save.isPending} onClick={() => form.submit()}>
              Save
            </Button>
          )}
        </Space>
      }
    >
      {formErrors.length > 0 && <Alert type="error" showIcon className="mb-4" message={formErrors.join(' ')} />}

      <Form
        form={form}
        layout="vertical"
        disabled={readOnly}
        initialValues={
          mode === 'create'
            ? { accessRole: 'NONE' }
            : {
                name: officer?.name,
                phone: officer?.phone,
                email: officer?.email,
                serviceNumber: officer?.serviceNumber,
                accessRole: officer?.accessRole,
              }
        }
        onFinish={(values: Record<string, unknown>) => {
          /**
           * ⚠️ A blank email is sent as `undefined`, NEVER `''`.
           *
           * FR-5.8.2's partial unique index reaches all the way into this serialiser:
           * an empty string IS a string, so two blank-email officers would collide on
           * the index. The model omits the field too, but the request should never have
           * carried it in the first place.
           */
          const cleaned = { ...values };
          for (const key of ['email', 'serviceNumber', 'photoUrl'] as const) {
            const value = cleaned[key];
            const isBlank = value === undefined || value === null || (typeof value === 'string' && value.trim() === '');
            if (!isBlank) continue;

            if (mode === 'edit' && officer?.[key]) {
              // An explicitly emptied field on edit means CLEAR IT, which the API
              // expresses as null and turns into a real `$unset`.
              cleaned[key] = null;
            } else {
              /**
               * DELETED, not set to `undefined`.
               *
               * `{ email: undefined }` STILL HAS THE KEY, and zod's `.optional()` carries
               * an explicitly-present undefined straight through to the output — verified,
               * not assumed. AntD hands untouched fields to `onFinish` exactly that way,
               * so the request body would carry `email` and only `JSON.stringify` dropping
               * it would save us. Relying on that is the kind of accident FR-5.8.2 cannot
               * afford, so the key goes.
               */
              delete cleaned[key];
            }
          }

          const parsed = submit(cleaned);
          if (parsed.ok) save.mutate(parsed.data);
        }}
      >
        <Form.Item {...field('name')} label="Name">
          <Input placeholder="Full name as recorded by the unit" />
        </Form.Item>

        <Form.Item
          {...field('phone')}
          label="Phone"
          tooltip="Paste any format — it is normalised in place."
          /*
            ⚠️ [FIX-2] VALIDATE ON SUBMIT ONLY, AND `validateTrigger={[]}` IS WHAT DOES IT.
            AntD's default is `onChange`, so a number went red at the FIRST keystroke and
            stayed red the whole way to the tenth — telling an operator they are wrong while
            they are still typing. An empty trigger list removes every automatic trigger;
            `form.validateFields()`, which `onFinish` runs, is unaffected, so the rule still
            fires on submit and the error still arrives before any request is sent.
          */
          validateTrigger={[]}
        >
          <PhoneInput />
        </Form.Item>

        <Form.Item {...field('email')} label="Email" tooltip="Optional, and unique when present.">
          <Input type="email" placeholder="Optional" autoComplete="email" />
        </Form.Item>

        <Form.Item {...field('serviceNumber')} label="Service / collar number">
          <Input placeholder="Optional" />
        </Form.Item>

        {mode === 'create' && (
          <>
            <Form.Item {...field('rankId')} label="Rank">
              <RankPicker />
            </Form.Item>
            <Form.Item {...field('orgUnitId')} label="Posted to">
              {/* [C5c §11c] An elevated caller may not create an officer AT the level they act at. */}
              <OrgUnitPicker disabledIds={actor?.actingOrgUnit ? [actor.actingOrgUnit.id] : []} />
            </Form.Item>
          </>
        )}

        <Form.Item
          {...field('accessRole')}
          label="Dashboard access"
          tooltip="Mobile only means the officer app only. You cannot grant a role at or above your own."
        >
          <Select
            /*
             * ⚠️ [D4.5 §1b] THE OFFICER'S CURRENT ROLE IS ALWAYS AN OPTION, even when it is
             * one this actor may not GRANT. `grantableRoles` excludes roles at or above the
             * caller's own, so editing a peer rendered the bare enum member — `NODAL_ADMIN`
             * rather than "nodal admin". Not an ObjectId, and the same shape: a value with no
             * option falls through to itself (`lib/selectOptions.ts`).
             *
             * ⚠️ IT DOES NOT WIDEN THE GRANT. The extra entry is only ever the value already
             * on the record, so choosing it is a no-op; the API applies the same table and
             * still refuses an actual escalation. Everything else on the list is unchanged.
             */
            options={withChosenOption(
              grantableRoles.map((role) => ({ value: role, label: roleLabel(role) })),
              officer?.accessRole,
              (role) => ({ name: roleLabel(role as AccessRole) }),
            )}
          />
        </Form.Item>
      </Form>
      {/* [C5c §11h, amended] Beneath the permissions field: the posting unit's nodal access level,
          READ-ONLY — it is the unit's setting, set on the Organisation page. Edit only: a new
          officer has no posting until they are created. */}
      {mode === 'edit' && officer && (
        <UnitElevationReadout orgUnitId={officer.orgUnitId} actingAt={officer.actingOrgUnit?.name} />
      )}
    </Drawer>
  );
};
